import React from "react";
import { Users as UsersIcon, ShieldX, ShieldOff, CheckCircle2 } from "lucide-react";
import { UserProfilePanel } from "../UserProfilePanel";
import { AdminStatusPill, adminFmtDate, AdminSectionHeading } from "./AdminPanelShared";

/**
 * Users registry tab. Displays the citizen list with date-joined filters,
 * sort options, status pill, and the per-user action triplet.
 */
export const AdminUsersTab = ({
  users,
  usersHasMore,
  usersFilter,
  onFiltersChange,
  onApplyFilters,
  onResetFilters,
  onSetStatus,
  onLoadMore,
  loadingMore,
  token,
}) => {
  const fmt = adminFmtDate;
  return (
    <div className="px-3 sm:px-5 py-4 pb-8 space-y-2" data-testid="admin-users-list">
      <AdminSectionHeading icon={UsersIcon} label="Registre citoyens" count={users.length} />

      {/* Filter bar — period preset / custom range / sort. */}
      <div
        className="border border-zinc-800 bg-[#0f1318] p-2.5 space-y-2"
        data-testid="users-filter-bar"
      >
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mr-1">
            Période
          </span>
          {[
            ["all", "Tous"], ["today", "Aujourd'hui"], ["7d", "7 j"],
            ["30d", "30 j"], ["this_month", "Ce mois"], ["custom", "Custom"],
          ].map(([id, label]) => (
            <button
              key={id}
              data-testid={`users-preset-${id}`}
              onClick={() => onFiltersChange((f) => ({ ...f, preset: id }))}
              className={`px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] border ${
                usersFilter.preset === id
                  ? "bg-emerald-500 text-black border-emerald-500"
                  : "border-zinc-700 text-zinc-400 hover:text-zinc-100"
              }`}
            >
              {label}
            </button>
          ))}
          {usersFilter.preset === "custom" && (
            <div className="flex items-center gap-1 ml-1" data-testid="users-custom-range">
              <input
                type="datetime-local"
                value={usersFilter.customSince}
                onChange={(e) => onFiltersChange((f) => ({ ...f, customSince: e.target.value }))}
                data-testid="users-custom-since"
                aria-label="Date de début"
                className="bg-[#0b0f14] border border-zinc-700 text-zinc-200 px-1.5 py-1 text-[10px] font-mono focus:border-emerald-500 focus:outline-none"
              />
              <span className="text-zinc-500">→</span>
              <input
                type="datetime-local"
                value={usersFilter.customUntil}
                onChange={(e) => onFiltersChange((f) => ({ ...f, customUntil: e.target.value }))}
                data-testid="users-custom-until"
                aria-label="Date de fin"
                className="bg-[#0b0f14] border border-zinc-700 text-zinc-200 px-1.5 py-1 text-[10px] font-mono focus:border-emerald-500 focus:outline-none"
              />
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mr-1">
            Trier
          </span>
          <select
            value={`${usersFilter.sortBy}:${usersFilter.sortOrder}`}
            onChange={(e) => {
              const [sb, so] = e.target.value.split(":");
              onFiltersChange((f) => ({ ...f, sortBy: sb, sortOrder: so }));
            }}
            data-testid="users-sort-select"
            className="bg-[#0b0f14] border border-zinc-700 text-zinc-200 px-2 py-1 text-[10px] font-mono focus:border-emerald-500 focus:outline-none"
          >
            <option value="updated_at:desc">Récente activité (récent → ancien)</option>
            <option value="created_at:desc">Date d&apos;inscription (récent → ancien)</option>
            <option value="created_at:asc">Date d&apos;inscription (ancien → récent)</option>
          </select>
          <button
            type="button"
            data-testid="users-apply-filters"
            onClick={onApplyFilters}
            className="ml-auto border border-emerald-500/60 bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.15em]"
          >
            Appliquer
          </button>
          <button
            type="button"
            data-testid="users-reset-filters"
            onClick={onResetFilters}
            className="border border-zinc-700 hover:border-amber-500 text-zinc-300 hover:text-amber-300 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.15em]"
          >
            Réinitialiser
          </button>
        </div>
      </div>

      {users.length === 0 && (
        <div className="text-xs text-zinc-500">Aucun utilisateur enregistré sur cette période.</div>
      )}
      {users.map((u) => (
        <div
          key={u.phone}
          className="border border-zinc-800 bg-[#0f1318] hover:border-emerald-500/40 transition-colors p-3"
          data-testid={`admin-user-${u.phone}`}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="font-mono text-sm font-bold text-zinc-100 truncate tracking-wider">
              {u.phone}
            </div>
            <AdminStatusPill s={u.status} />
          </div>
          <div className="text-[11px] text-zinc-500 mt-1.5 grid grid-cols-2 sm:grid-cols-4 gap-2 font-mono">
            <div>
              <span className="font-bold text-emerald-400 tabular-nums">{u.total_alerts}</span>{" "}
              <span className="text-zinc-600">alertes</span>
            </div>
            <div>
              <span className="font-bold text-rose-400 tabular-nums">{u.false_alerts}</span>{" "}
              <span className="text-zinc-600">fausses</span>
            </div>
            <div className="truncate tabular-nums" title="Activité">{fmt(u.updated_at)}</div>
            <div
              className="truncate tabular-nums"
              data-testid={`user-joined-${u.phone}`}
              title="Date d'inscription"
            >
              <span className="text-zinc-600">Inscrit </span>{fmt(u.created_at)}
            </div>
          </div>
          {u.note && <div className="text-[11px] text-zinc-500 mt-1 italic">« {u.note} »</div>}
          <div className="grid grid-cols-3 gap-1.5 mt-3">
            <button
              onClick={() => onSetStatus(u.phone, "active")}
              data-testid={`admin-set-active-${u.phone}`}
              disabled={u.status === "active"}
              className="border border-emerald-500/50 text-emerald-300 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-emerald-500 hover:text-black transition-colors disabled:opacity-40 flex items-center justify-center gap-1"
            >
              <CheckCircle2 className="w-3 h-3" /> Actif
            </button>
            <button
              onClick={() => onSetStatus(u.phone, "suspect")}
              data-testid={`admin-set-suspect-${u.phone}`}
              disabled={u.status === "suspect"}
              className="border border-amber-500/50 text-amber-300 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-amber-500 hover:text-black transition-colors disabled:opacity-40 flex items-center justify-center gap-1"
            >
              <ShieldOff className="w-3 h-3" /> Suspect
            </button>
            <button
              onClick={() => onSetStatus(u.phone, "suspended")}
              data-testid={`admin-set-suspend-${u.phone}`}
              disabled={u.status === "suspended"}
              className="border border-rose-500/50 text-rose-300 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-rose-500 hover:text-white transition-colors disabled:opacity-40 flex items-center justify-center gap-1"
            >
              <ShieldX className="w-3 h-3" /> Suspendre
            </button>
          </div>
          <UserProfilePanel phone={u.phone} />
        </div>
      ))}
      {usersHasMore && (
        <button
          type="button"
          data-testid="admin-users-load-more"
          onClick={onLoadMore}
          disabled={loadingMore}
          className="w-full border border-zinc-700 text-zinc-300 py-2.5 text-[10px] font-bold uppercase tracking-[0.2em] hover:border-emerald-500 hover:text-emerald-300 transition-colors disabled:opacity-60"
        >
          {loadingMore ? "Chargement…" : "Charger plus"}
        </button>
      )}
    </div>
  );
};
